// 표 칸 방향키 이동(2026-09-23 오너 요청 "셀 내에서 방향키로 이동도 됐으면 좋겠다").
//
// 엑셀과 같게:
// - 칸을 누르거나 바로 글자를 치는 중이면 방향키는 **옆 칸으로 옮긴다**(엑셀 '입력' 상태).
// - 두 번 누르거나 F2 를 누르면 '편집' 상태 — 방향키가 글자 사이 커서를 옮긴다. 이때도
//   글자 맨 앞에서 ←·↑, 맨 끝에서 →·↓ 면 옆 칸으로 나간다(한 칸 안에서 갇히지 않게).
// - Shift+방향키는 고른 범위를 늘린다(시작 칸은 그대로).
// - 병합 칸은 한 칸으로 친다 — 병합 너머의 칸으로 한 번에 건너간다.
// - 다른 입력창(속성 패널 등)에서는 건드리지 않는다.

import { useEffect, useRef, type MutableRefObject } from 'react';
import { layoutMasterOf, type LayoutTable } from '@bhops/shared';
import type { CellRange } from './useLayoutEditor';

export interface CellArrowEditor {
  selected: { kind: string; id: string } | null;
  cellRange: CellRange | null;
  setCellRange: (r: CellRange | null) => void;
}

type Dir = 'up' | 'down' | 'left' | 'right';
const DIRS: Record<string, Dir> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

/** (r,c) 에서 dir 쪽 다음 칸(병합은 건너뛴다). 표 끝이면 null. */
export function stepCell(
  t: LayoutTable,
  pos: { r: number; c: number },
  dir: Dir,
): { r: number; c: number } | null {
  const m = layoutMasterOf(t, pos.r, pos.c);
  const cell = t.cells[m.r][m.c];
  const rs = cell.rowSpan ?? 1;
  const cs = cell.colSpan ?? 1;
  // 병합 칸 안에서 옆으로 갈 때는 지금 줄·열을 지킨다(엑셀처럼) — 주인 칸 좌표가 아니라.
  let r = pos.r;
  let c = pos.c;
  if (dir === 'up') r = m.r - 1;
  if (dir === 'down') r = m.r + rs;
  if (dir === 'left') c = m.c - 1;
  if (dir === 'right') c = m.c + cs;
  if (r < 0 || c < 0 || r >= t.rowHeights.length || c >= t.colWidths.length) return null;
  return layoutMasterOf(t, r, c);
}

const isCellEditor = (el: Element | null): el is HTMLTextAreaElement =>
  el instanceof HTMLTextAreaElement && el.dataset.cellEditor === '1';

const isOtherInput = (el: Element | null) =>
  !isCellEditor(el) &&
  (el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement ||
    (el instanceof HTMLElement && el.isContentEditable));

/** 편집 상태에서 커서가 그 방향의 끝에 있는가(글자 선택이 없을 때만). */
function caretAtEdge(ta: HTMLTextAreaElement, dir: Dir): boolean {
  const { selectionStart: s, selectionEnd: e, value } = ta;
  if (s !== e) return false;
  if (dir === 'left') return s === 0;
  if (dir === 'right') return s === value.length;
  if (dir === 'up') return !value.slice(0, s).includes('\n');
  return !value.slice(s).includes('\n');
}

export function useCellArrowKeys(editorRef: MutableRefObject<CellArrowEditor>): void {
  /** 편집 상태인 칸 — `표id:행,열`. 칸이 바뀌면 풀린다. */
  const editKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const keyOf = () => {
      const ed = editorRef.current;
      const a = ed.cellRange?.a;
      return ed.selected && a ? `${ed.selected.id}:${a.r},${a.c}` : null;
    };

    function onDblClick(e: MouseEvent) {
      if (isCellEditor(e.target as Element)) editKeyRef.current = keyOf();
    }

    function onKeyDown(e: KeyboardEvent) {
      const ed = editorRef.current;
      const t = ed.selected;
      const range = ed.cellRange;
      if (!t || t.kind !== 'table' || !range) return;
      const focus = document.activeElement;
      if (isOtherInput(focus)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'F2' && isCellEditor(focus)) {
        e.preventDefault();
        editKeyRef.current = keyOf();
        const end = focus.value.length;
        focus.setSelectionRange(end, end);
        return;
      }

      const dir = DIRS[e.key];
      if (!dir) return;
      // 편집 상태면 커서가 끝에 닿았을 때만 칸을 옮긴다.
      if (isCellEditor(focus) && editKeyRef.current === keyOf() && !caretAtEdge(focus, dir)) return;

      const table = t as LayoutTable;
      if (e.shiftKey) {
        const next = stepCell(table, range.b, dir);
        e.preventDefault();
        e.stopPropagation();
        if (next) ed.setCellRange({ a: range.a, b: next });
        return;
      }
      const next = stepCell(table, range.a, dir);
      e.preventDefault();
      e.stopPropagation();
      if (!next) return;
      editKeyRef.current = null;
      ed.setCellRange({ a: next, b: next });
    }

    // 캡처 단계 — 디자이너의 필드 방향키(필드 옮기기)보다 먼저 받는다.
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('dblclick', onDblClick, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('dblclick', onDblClick, true);
    };
  }, [editorRef]);
}
